import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Eye, Clock, Globe, Monitor, Smartphone, Tablet,
  Chrome, ArrowUpRight, ArrowDownRight, RefreshCw, Calendar,
  MapPin, Link2, FileText, User, Activity, TrendingUp, BarChart3,
  ChevronLeft, ChevronRight, Search, Filter, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to format time duration
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

// Helper to format relative time
const formatRelativeTime = (isoString) => {
  if (!isoString) return 'Unknown';
  const date = new Date(isoString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

// Device icon component
const DeviceIcon = ({ type }) => {
  switch (type?.toLowerCase()) {
    case 'mobile': return <Smartphone className="h-4 w-4" />;
    case 'tablet': return <Tablet className="h-4 w-4" />;
    default: return <Monitor className="h-4 w-4" />;
  }
};

// Browser icon/badge
const BrowserBadge = ({ browser }) => {
  const colors = {
    'Chrome': 'bg-green-500/20 text-green-400',
    'Safari': 'bg-blue-500/20 text-blue-400',
    'Firefox': 'bg-orange-500/20 text-orange-400',
    'Edge': 'bg-cyan-500/20 text-cyan-400',
    'Opera': 'bg-red-500/20 text-red-400',
    'default': 'bg-gray-500/20 text-gray-400'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[browser] || colors['default']}`}>
      {browser || 'Unknown'}
    </span>
  );
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorDetails, setVisitorDetails] = useState(null);
  const [dateRange, setDateRange] = useState('7');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [activeTab, setActiveTab] = useState('overview'); // overview, visitors, details
  const [searchTerm, setSearchTerm] = useState('');

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/dashboard?days=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.status === 403) {
        toast.error('Admin access required');
        navigate('/admin');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load analytics');
    }
  }, [dateRange, navigate]);

  // Fetch visitors list
  const fetchVisitors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/visitors?page=${currentPage}&limit=20`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setVisitors(data.visitors);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch visitors:', error);
    }
  }, [currentPage]);

  // Fetch visitor details
  const fetchVisitorDetails = async (visitorId) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/visitor/${visitorId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setVisitorDetails(data);
        setActiveTab('details');
      }
    } catch (error) {
      console.error('Failed to fetch visitor details:', error);
      toast.error('Failed to load visitor details');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchVisitors()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchVisitors]);

  // Refresh data
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchVisitors()]);
    setLoading(false);
    toast.success('Analytics refreshed');
  };

  // Filter visitors by search
  const filteredVisitors = visitors.filter(v => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      v.country?.toLowerCase().includes(search) ||
      v.city?.toLowerCase().includes(search) ||
      v.browser?.toLowerCase().includes(search) ||
      v.user_info?.email?.toLowerCase().includes(search) ||
      v.visitor_id?.toLowerCase().includes(search)
    );
  });

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-blue-400" />
              Visitor Analytics
            </h1>
            <p className="text-white/60 text-sm mt-1">Track and analyze your website visitors</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px] bg-white/5 border-white/20 text-white">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                <SelectItem value="1" className="text-white">Today</SelectItem>
                <SelectItem value="7" className="text-white">Last 7 days</SelectItem>
                <SelectItem value="30" className="text-white">Last 30 days</SelectItem>
                <SelectItem value="90" className="text-white">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={loading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-blue-600' : 'text-white/60 hover:text-white'}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Overview
          </Button>
          <Button
            variant={activeTab === 'visitors' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('visitors')}
            className={activeTab === 'visitors' ? 'bg-blue-600' : 'text-white/60 hover:text-white'}
          >
            <Users className="h-4 w-4 mr-2" />
            Visitors
          </Button>
          {visitorDetails && (
            <Button
              variant={activeTab === 'details' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('details')}
              className={activeTab === 'details' ? 'bg-blue-600' : 'text-white/60 hover:text-white'}
            >
              <User className="h-4 w-4 mr-2" />
              Visitor Details
            </Button>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-2">
                    <Users className="h-4 w-4" />
                    <span className="text-xs">Visitors</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{dashboardData.summary.total_visitors.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <Activity className="h-4 w-4" />
                    <span className="text-xs">Sessions</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{dashboardData.summary.total_sessions.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-purple-400 mb-2">
                    <Eye className="h-4 w-4" />
                    <span className="text-xs">Page Views</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{dashboardData.summary.total_pageviews.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-cyan-400 mb-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-xs">Avg. Duration</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{formatDuration(dashboardData.summary.avg_session_duration)}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-yellow-400 mb-2">
                    <ArrowUpRight className="h-4 w-4" />
                    <span className="text-xs">New</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{dashboardData.summary.new_visitors.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-rose-500/10 to-red-500/10 border-rose-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-rose-400 mb-2">
                    <ArrowDownRight className="h-4 w-4" />
                    <span className="text-xs">Returning</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{dashboardData.summary.returning_visitors.toLocaleString()}</div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Trend */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-400" />
                    Daily Visitors Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-end gap-1">
                    {dashboardData.daily_trend.map((day, i) => {
                      const maxVisitors = Math.max(...dashboardData.daily_trend.map(d => d.visitors)) || 1;
                      const height = (day.visitors / maxVisitors) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t hover:from-blue-500 hover:to-blue-300 transition-colors cursor-pointer"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${day.date}: ${day.visitors} visitors`}
                          />
                          <span className="text-[10px] text-white/40">{day.date.split('-')[2]}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Pages */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-400" />
                    Top Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {dashboardData.top_pages.map((page, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5 hover:bg-white/10">
                        <span className="text-white/80 text-sm truncate flex-1 mr-2">{page.page || '/'}</span>
                        <span className="text-blue-400 text-sm font-medium">{page.views.toLocaleString()}</span>
                      </div>
                    ))}
                    {dashboardData.top_pages.length === 0 && (
                      <p className="text-white/40 text-center py-4">No data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Breakdown Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* By Country */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-xs flex items-center gap-2">
                    <Globe className="h-3 w-3 text-cyan-400" />
                    Countries
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-40 overflow-y-auto">
                  {dashboardData.by_country.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/70">{item.country}</span>
                      <span className="text-cyan-400">{item.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* By Browser */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-xs flex items-center gap-2">
                    <Chrome className="h-3 w-3 text-green-400" />
                    Browsers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-40 overflow-y-auto">
                  {dashboardData.by_browser.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/70">{item.browser}</span>
                      <span className="text-green-400">{item.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* By Device */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-xs flex items-center gap-2">
                    <Monitor className="h-3 w-3 text-purple-400" />
                    Devices
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-40 overflow-y-auto">
                  {dashboardData.by_device.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/70 flex items-center gap-1">
                        <DeviceIcon type={item.device} />
                        {item.device}
                      </span>
                      <span className="text-purple-400">{item.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Top Referrers */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-xs flex items-center gap-2">
                    <Link2 className="h-3 w-3 text-orange-400" />
                    Referrers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-40 overflow-y-auto">
                  {dashboardData.top_referrers.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/70 truncate flex-1 mr-2">{item.referrer}</span>
                      <span className="text-orange-400">{item.count}</span>
                    </div>
                  ))}
                  {dashboardData.top_referrers.length === 0 && (
                    <p className="text-white/40 text-center py-2 text-xs">No referrer data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by country, city, browser, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/40"
                />
              </div>
              <span className="text-white/60 text-sm">{pagination.total} total visitors</span>
            </div>

            {/* Visitors Table */}
            <Card className="bg-white/5 border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Visitor</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Location</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Device</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Pages</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Duration</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Last Active</th>
                      <th className="text-left text-xs text-white/60 font-medium p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisitors.map((visitor, i) => (
                      <tr 
                        key={visitor.session_id || i} 
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              visitor.is_logged_in 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-white/10 text-white/60'
                            }`}>
                              <User className="h-4 w-4" />
                            </div>
                            <div>
                              {visitor.is_logged_in ? (
                                <>
                                  <div className="text-white text-sm font-medium">{visitor.user_info?.name || 'User'}</div>
                                  <div className="text-white/40 text-xs">{visitor.user_info?.email}</div>
                                </>
                              ) : (
                                <>
                                  <div className="text-white/70 text-sm">Anonymous</div>
                                  <div className="text-white/40 text-xs font-mono">{visitor.visitor_id?.slice(0, 12)}...</div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-white/70 text-sm">
                            <MapPin className="h-3 w-3" />
                            {visitor.city}, {visitor.country}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <DeviceIcon type={visitor.device_type} />
                            <BrowserBadge browser={visitor.browser} />
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-white/70 text-sm">{visitor.page_views} pages</span>
                        </td>
                        <td className="p-3">
                          <span className="text-white/70 text-sm">{formatDuration(visitor.total_time)}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-white/50 text-sm">{formatRelativeTime(visitor.last_activity)}</span>
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedVisitor(visitor);
                              fetchVisitorDetails(visitor.visitor_id);
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-7 px-2"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredVisitors.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-white/40">
                          No visitors found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-white/10">
                  <span className="text-white/60 text-sm">
                    Page {currentPage} of {pagination.pages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="border-white/20 text-white"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                      disabled={currentPage === pagination.pages}
                      className="border-white/20 text-white"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Visitor Details Tab */}
        {activeTab === 'details' && visitorDetails && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              onClick={() => setActiveTab('visitors')}
              className="text-white/60 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Visitors
            </Button>

            {/* Visitor Summary */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedVisitor?.is_logged_in 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-white/10 text-white/60'
                  }`}>
                    <User className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-white text-lg font-semibold">
                      {selectedVisitor?.user_info?.name || 'Anonymous Visitor'}
                    </h2>
                    <p className="text-white/60 text-sm font-mono">{visitorDetails.visitor_id}</p>
                    {selectedVisitor?.user_info?.email && (
                      <p className="text-white/50 text-sm">{selectedVisitor.user_info.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 text-xs mb-1">Total Sessions</div>
                    <div className="text-white text-xl font-bold">{visitorDetails.stats.total_sessions}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 text-xs mb-1">Total Page Views</div>
                    <div className="text-white text-xl font-bold">{visitorDetails.stats.total_pageviews}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 text-xs mb-1">Time Spent</div>
                    <div className="text-white text-xl font-bold">{formatDuration(visitorDetails.stats.total_time_spent)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 text-xs mb-1">First Visit</div>
                    <div className="text-white text-sm">{new Date(visitorDetails.stats.first_visit).toLocaleDateString()}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="text-white/50 text-xs mb-1">Last Visit</div>
                    <div className="text-white text-sm">{formatRelativeTime(visitorDetails.stats.last_visit)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sessions History */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm">Session History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                {visitorDetails.sessions.map((session, i) => (
                  <div key={session.session_id || i} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/70 text-sm">
                        {new Date(session.started_at).toLocaleString()}
                      </span>
                      <span className="text-white/50 text-xs">
                        {formatDuration(session.total_time)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-white/50">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" /> {session.page_views} pages
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {session.city}, {session.country}
                      </span>
                      <span className="flex items-center gap-1">
                        <DeviceIcon type={session.device_type} /> {session.browser}
                      </span>
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-white/40">Entry:</span>{' '}
                      <span className="text-white/70">{session.entry_page}</span>
                      {session.exit_page !== session.entry_page && (
                        <>
                          <span className="text-white/40 mx-2">→</span>
                          <span className="text-white/40">Exit:</span>{' '}
                          <span className="text-white/70">{session.exit_page}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Recent Page Views */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm">Recent Page Views</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                {visitorDetails.recent_pageviews.map((pv, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5 text-sm">
                    <span className="text-white/70 truncate flex-1">{pv.page_url}</span>
                    <span className="text-white/40 text-xs ml-2">{formatRelativeTime(pv.timestamp)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
